(() => {
  const carouselInstances = new WeakMap();
  const comparisonInstances = new WeakMap();
  const satietyInstances = new WeakMap();
  const reviewVideoInstances = new WeakMap();

  function initCarousel(root) {
    const carousel = root.querySelector('[data-testimonial-carousel]');
    if (!carousel || carouselInstances.has(carousel)) return;

    const track = carousel.querySelector('[data-testimonial-track]');
    const originals = Array.from(track.children);
    const previousButton = carousel.querySelector('[data-carousel-prev]');
    const nextButton = carousel.querySelector('[data-carousel-next]');
    const dots = Array.from(carousel.querySelectorAll('[data-carousel-dot]'));
    const desktopQuery = window.matchMedia('(min-width: 768px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeIndex = 0;
    let cycleWidth = 0;
    let frameId = 0;
    let lastFrameTime = performance.now();
    let pauseUntil = performance.now() + 1500;
    let pointerActive = false;
    let focusActive = false;
    let hoverActive = false;
    let scrollFrame = 0;
    // PS-3: track whether we have suspended the rail's scroll-snap for the auto-motion tween, so
    // the DOM is only touched on an actual transition (never every frame).
    let snapSuspended = false;
    // PS-3: float accumulator for the marquee position. The browser quantises `scrollLeft` to
    // whole pixels, so a raw sub-pixel `scrollLeft += 0.4` per frame rounds back and never
    // advances. We accumulate the true position here and write the rounded value to the rail.
    let scrollPos = 0;

    // PS-3: the mobile cards are zoomed by `zoom: var(--s)`, which scales layout metrics. Under
    // zoom `offsetLeft` is unreliable across engines, while getBoundingClientRect() reflects the
    // zoom and shares units with the (unzoomed) rail's scrollLeft/scrollWidth. Measure geometry
    // with rects so the marquee wrap point and arrow/dot targets stay correct at every width.
    function cardScrollLeft(card) {
      return track.scrollLeft + (card.getBoundingClientRect().left - track.getBoundingClientRect().left);
    }

    // PS-3: while the marquee auto-moves, suspend BOTH of the mobile rail's manual-scroll aids:
    //  - `scroll-snap-type: x mandatory` (a JS scrollLeft tween fights mandatory snap), and
    //  - `scroll-behavior: smooth` (each per-frame scrollLeft write would start a fresh smooth
    //    animation that never lands, freezing the rail near 0 on mobile).
    // Restore both when paused/interacting so a manual swipe still snaps and arrow taps animate
    // smoothly. Modeled on the buy-box gallery snap-suspension pattern. The DOM is only touched on
    // an actual transition (guarded by snapSuspended). Desktop's rail is already scroll-behavior
    // auto / no snap, so this is a no-op there.
    function setSnap(suspend) {
      if (suspend === snapSuspended) return;
      snapSuspended = suspend;
      track.style.scrollSnapType = suspend ? 'none' : '';
      track.style.scrollBehavior = suspend ? 'auto' : '';
    }

    function removeClones() {
      track.querySelectorAll('.cowmit-satiety-v1__testimonial-clone').forEach((clone) => clone.remove());
      cycleWidth = 0;
    }

    function measureCycle() {
      const firstClone = track.querySelector('.cowmit-satiety-v1__testimonial-clone');
      cycleWidth = firstClone && originals[0]
        ? firstClone.getBoundingClientRect().left - originals[0].getBoundingClientRect().left
        : 0;
    }

    function addClones() {
      if (!track.querySelector('.cowmit-satiety-v1__testimonial-clone')) {
        originals.forEach((card) => {
          const clone = card.cloneNode(true);
          clone.classList.add('cowmit-satiety-v1__testimonial-clone');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a, button, [tabindex]').forEach((interactive) => interactive.setAttribute('tabindex', '-1'));
          track.appendChild(clone);
        });
      }
      measureCycle();
    }

    function updateControls() {
      dots.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
      if (previousButton) previousButton.disabled = activeIndex === 0;
      if (nextButton) nextButton.disabled = activeIndex === originals.length - 1;
    }

    function nearestIndex() {
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;
      originals.forEach((card, index) => {
        const currentDistance = Math.abs(cardScrollLeft(card) - track.scrollLeft);
        if (currentDistance < distance) {
          nearest = index;
          distance = currentDistance;
        }
      });
      return nearest;
    }

    function updateFromScroll() {
      // Skip while the auto-marquee runs: its scrollLeft writes fire a scroll event every frame,
      // and the dots are hidden during the loop, so running nearestIndex() (many getBoundingClientRect
      // reads) per frame is wasted layout work that makes the motion stutter/lag.
      if (desktopQuery.matches || carousel.classList.contains('marquee-on')) return;
      activeIndex = nearestIndex();
      updateControls();
    }

    function scheduleScrollUpdate() {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(updateFromScroll);
    }

    function goTo(index) {
      activeIndex = Math.max(0, Math.min(originals.length - 1, index));
      const target = originals[activeIndex];
      // A manual jump should snap, so restore snap before scrolling and pause the auto-motion.
      setSnap(false);
      track.scrollTo({ left: cardScrollLeft(target), behavior: reducedMotionQuery.matches ? 'auto' : 'smooth' });
      pauseUntil = performance.now() + 3000;
      updateControls();
    }

    // Feed the CSS keyframe marquee: --marquee-cycle = one full card-set width, --marquee-duration =
    // that width / 80px per second. Cleared when motion is off so the animation has nothing to run.
    function applyMarqueeSpeed() {
      if (reducedMotionQuery.matches || !(cycleWidth > 0)) {
        track.style.removeProperty('--marquee-cycle');
        track.style.removeProperty('--marquee-duration');
        return;
      }
      track.style.setProperty('--marquee-cycle', cycleWidth + 'px');
      track.style.setProperty('--marquee-duration', (cycleWidth / 80) + 's');
    }

    function syncMode() {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
        frameId = 0;
      }
      // Rebuild clones from scratch so cycleWidth is re-measured for the new viewport/zoom.
      removeClones();
      track.scrollLeft = 0;
      scrollPos = 0;
      activeIndex = 0;
      carousel.classList.toggle('is-reduced-motion', reducedMotionQuery.matches);
      // marquee-on drives the CSS transform animation (compositor-smooth, replaces the old per-frame
      // scrollLeft tween) AND hides the manual arrows/dots. Set only when JS is present and motion is
      // allowed, so a no-JS or reduced-motion visitor keeps the manual scroll rail + controls.
      carousel.classList.toggle('marquee-on', !reducedMotionQuery.matches);
      updateControls();
      if (!reducedMotionQuery.matches) {
        addClones();          // duplicates the cards + measures cycleWidth
      }
      applyMarqueeSpeed();     // the GPU keyframe animation does the motion; no per-frame JS
    }

    function animate(now) {
      const delta = Math.min(now - lastFrameTime, 64);
      lastFrameTime = now;
      if (reducedMotionQuery.matches) { frameId = 0; return; }
      // Client request 2026-08-20: the reviews marquee NEVER pauses (no hover/scroll/focus stop) and
      // runs faster. Only prefers-reduced-motion (a11y) still disables it (handled above).
      const paused = false;
      if (!paused && cycleWidth > 0) {
        setSnap(true);
        scrollPos += delta * 0.08;
        if (scrollPos >= cycleWidth) scrollPos -= cycleWidth;
        track.scrollLeft = scrollPos;
      } else {
        setSnap(false);
        // Follow manual scroll / snap / goTo so auto-motion resumes from the current position.
        scrollPos = track.scrollLeft;
      }
      frameId = window.requestAnimationFrame(animate);
    }

    function handlePointerUp() {
      pointerActive = false;
      pauseUntil = performance.now() + 3000;
    }

    previousButton?.addEventListener('click', () => goTo(activeIndex - 1));
    nextButton?.addEventListener('click', () => goTo(activeIndex + 1));
    track.addEventListener('scroll', scheduleScrollUpdate, { passive: true });
    track.addEventListener('pointerdown', () => {
      pointerActive = true;
      pauseUntil = performance.now() + 3000;
    });
    window.addEventListener('pointerup', handlePointerUp);
    track.addEventListener('mouseenter', () => { hoverActive = true; });
    track.addEventListener('mouseleave', () => {
      hoverActive = false;
      pauseUntil = performance.now() + 1200;
    });
    track.addEventListener('focusin', () => { focusActive = true; });
    track.addEventListener('focusout', () => {
      focusActive = false;
      pauseUntil = performance.now() + 2500;
    });
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(activeIndex + 1);
      }
    });
    track.addEventListener('wheel', () => { pauseUntil = performance.now() + 3000; }, { passive: true });
    desktopQuery.addEventListener('change', syncMode);
    reducedMotionQuery.addEventListener('change', syncMode);

    // PS-3: re-measure the seamless-loop cycle width when the rail resizes (mobile --s zoom
    // changes card size on every viewport-width change).
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => { if (!reducedMotionQuery.matches) { measureCycle(); applyMarqueeSpeed(); } })
      : null;
    resizeObserver?.observe(track);

    syncMode();
    carouselInstances.set(carousel, () => {
      window.cancelAnimationFrame(frameId);
      window.cancelAnimationFrame(scrollFrame);
      window.removeEventListener('pointerup', handlePointerUp);
      desktopQuery.removeEventListener('change', syncMode);
      reducedMotionQuery.removeEventListener('change', syncMode);
      resizeObserver?.disconnect();
      removeClones();
      carouselInstances.delete(carousel);
    });
  }

  function initComparisonCarousel(root) {
    const comparison = root.querySelector('[data-comparison-carousel]');
    if (!comparison || comparisonInstances.has(comparison)) return;

    const track = comparison.querySelector('[data-comparison-track]');
    if (!track) return;
    const previousButton = comparison.querySelector('[data-comparison-prev]');
    const nextButton = comparison.querySelector('[data-comparison-next]');
    const dots = Array.from(comparison.querySelectorAll('[data-comparison-dot]'));
    const productHeaders = Array.from(track.querySelectorAll('thead th:not(:first-child)'));
    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeIndex = 0;
    let scrollFrame = 0;

    // A native scroll carousel (like the satiety rail): CSS `scroll-snap-type: mandatory` on the
    // track drives the swipe, so a drag glides and lands on the next product column, with the pinned
    // 157px labels column staying put. This works reliably because the mobile table no longer uses
    // `zoom` (zoom was what broke scrollLeft/snapping). JS here only mirrors the scroll position into
    // the dots and moves the scroll for the arrow buttons / arrow keys.
    function productWidth() {
      return productHeaders[0] ? productHeaders[0].getBoundingClientRect().width : 1;
    }
    function updateControls() {
      dots.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
      if (previousButton) previousButton.disabled = activeIndex === 0;
      if (nextButton) nextButton.disabled = activeIndex === productHeaders.length - 1;
    }
    function nearestIndex() {
      return Math.max(0, Math.min(productHeaders.length - 1, Math.round(track.scrollLeft / productWidth())));
    }
    function onScroll() {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = 0;
        const index = nearestIndex();
        if (index !== activeIndex) { activeIndex = index; updateControls(); }
      });
    }
    function goTo(index) {
      activeIndex = Math.max(0, Math.min(productHeaders.length - 1, index));
      // Product i snaps at i * productWidth (scroll-padding-left equals the pinned labels width).
      track.scrollTo({ left: activeIndex * productWidth(), behavior: reducedMotionQuery.matches ? 'auto' : 'smooth' });
      updateControls();
    }
    function handlePrevious() { goTo(activeIndex - 1); }
    function handleNext() { goTo(activeIndex + 1); }
    function handleKeydown(event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); handlePrevious(); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); handleNext(); }
    }
    function syncMode() {
      activeIndex = 0;
      track.scrollLeft = 0;
      updateControls();
    }

    previousButton?.addEventListener('click', handlePrevious);
    nextButton?.addEventListener('click', handleNext);
    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('keydown', handleKeydown);
    mobileQuery.addEventListener('change', syncMode);

    syncMode();
    comparisonInstances.set(comparison, () => {
      window.cancelAnimationFrame(scrollFrame);
      previousButton?.removeEventListener('click', handlePrevious);
      nextButton?.removeEventListener('click', handleNext);
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('keydown', handleKeydown);
      mobileQuery.removeEventListener('change', syncMode);
      comparisonInstances.delete(comparison);
    });
  }

  function initSatietyRail(root) {
    const satiety = root.querySelector('[data-satiety-carousel]');
    if (!satiety || satietyInstances.has(satiety)) return;

    const rail = satiety.querySelector('.cowmit-satiety-v1__satiety-rail');
    if (!rail) return;
    const cards = Array.from(rail.querySelectorAll('.cowmit-satiety-v1__satiety-card'));
    const previousButton = satiety.querySelector('[data-satiety-prev]');
    const nextButton = satiety.querySelector('[data-satiety-next]');
    const dots = Array.from(satiety.querySelectorAll('[data-satiety-dot]'));
    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeIndex = 0;
    let scrollFrame = 0;
    let resizeFrame = 0;

    // PS-4: the cards are zoomed (`zoom: var(--s)`), which scales layout metrics. offsetLeft is
    // unreliable under zoom across engines, while getBoundingClientRect() reflects the zoom and
    // shares units with the rail's (unzoomed) scrollLeft/scrollWidth - same technique already used
    // by initCarousel's cardScrollLeft. Returns the card's left edge in absolute content
    // coordinates (i.e. independent of the rail's current scroll position).
    function cardScrollLeft(card) {
      return rail.scrollLeft + (card.getBoundingClientRect().left - rail.getBoundingClientRect().left);
    }

    function maxScrollLeft() {
      return Math.max(0, rail.scrollWidth - rail.clientWidth);
    }

    function updateControls() {
      dots.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
      if (previousButton) previousButton.disabled = activeIndex === 0;
      if (nextButton) nextButton.disabled = activeIndex === cards.length - 1;
    }

    function nearestIndex() {
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;
      cards.forEach((card, index) => {
        const currentDistance = Math.abs(cardScrollLeft(card) - rail.scrollLeft);
        if (currentDistance < distance) {
          nearest = index;
          distance = currentDistance;
        }
      });
      return nearest;
    }

    function updateFromScroll() {
      scrollFrame = 0;
      if (!mobileQuery.matches) return;
      const nextIndex = nearestIndex();
      if (nextIndex === activeIndex) return;
      activeIndex = nextIndex;
      updateControls();
    }

    function scheduleScrollUpdate() {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(updateFromScroll);
    }

    // PS-4: target scroll offset for a given card. The first card aligns by its LEADING edge
    // (mirrors the CSS `scroll-snap-align: start` + `scroll-padding-left`); the LAST card aligns
    // by its TRAILING edge and always targets the rail's exact max scroll offset. This mirrors the
    // CSS fix on `.satiety-card--normal` (`scroll-snap-align: end`, with `scroll-padding-right`
    // equal to the rail's `padding-right`) rather than re-deriving the same arithmetic in JS: the
    // last card's fully-visible resting position IS the max scroll position by construction, so
    // this is correct at every viewport width / --s zoom without hardcoding any pixel value.
    function targetFor(index) {
      if (index === cards.length - 1) return maxScrollLeft();
      const scrollPaddingLeft = parseFloat(getComputedStyle(rail).scrollPaddingLeft) || 0;
      return Math.max(0, Math.min(maxScrollLeft(), cardScrollLeft(cards[index]) - scrollPaddingLeft));
    }

    function goTo(index, smooth = true) {
      activeIndex = Math.max(0, Math.min(cards.length - 1, index));
      rail.scrollTo({
        left: targetFor(activeIndex),
        behavior: smooth && !reducedMotionQuery.matches ? 'smooth' : 'auto'
      });
      updateControls();
    }

    function alignCurrentPosition() {
      resizeFrame = 0;
      if (!mobileQuery.matches) {
        rail.scrollLeft = 0;
        activeIndex = 0;
        updateControls();
        return;
      }
      goTo(activeIndex, false);
    }

    function scheduleResizeAlignment() {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(alignCurrentPosition);
    }

    function syncMode() {
      activeIndex = 0;
      rail.scrollLeft = 0;
      updateControls();
    }

    function handlePrevious() {
      goTo(activeIndex - 1);
    }

    function handleNext() {
      goTo(activeIndex + 1);
    }

    function handleKeydown(event) {
      if (!mobileQuery.matches) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }
    }

    previousButton?.addEventListener('click', handlePrevious);
    nextButton?.addEventListener('click', handleNext);
    rail.addEventListener('scroll', scheduleScrollUpdate, { passive: true });
    rail.addEventListener('keydown', handleKeydown);
    mobileQuery.addEventListener('change', syncMode);
    reducedMotionQuery.addEventListener('change', scheduleResizeAlignment);

    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleResizeAlignment) : null;
    resizeObserver?.observe(rail);

    syncMode();
    satietyInstances.set(satiety, () => {
      window.cancelAnimationFrame(scrollFrame);
      window.cancelAnimationFrame(resizeFrame);
      previousButton?.removeEventListener('click', handlePrevious);
      nextButton?.removeEventListener('click', handleNext);
      rail.removeEventListener('scroll', scheduleScrollUpdate);
      rail.removeEventListener('keydown', handleKeydown);
      mobileQuery.removeEventListener('change', syncMode);
      reducedMotionQuery.removeEventListener('change', scheduleResizeAlignment);
      resizeObserver?.disconnect();
      satietyInstances.delete(satiety);
    });
  }

  // Click-to-play review videos (client 2026-08-21, revised). The video plays INLINE inside its own
  // review card - no full-screen overlay. On click the JS takes over the CSS marquee (which is a
  // GPU keyframe transform): it freezes the rail at its current offset, then glides the clicked card
  // to the centre of the viewport, so the whole rail (every other card) slides with it. The card's
  // own <video> plays with sound + native controls. Pausing or finishing the video hands motion back
  // to the marquee, resumed from the rail's current position with no jump (negative animation-delay
  // matched to the frozen phase). Because nothing is cloned or maximised, re-clicking after a loop
  // Just plays again - the earlier lightbox left an invisible overlay swallowing the next click.
  function initReviewVideoPlay(root) {
    const carousel = root.querySelector('[data-testimonial-carousel]');
    if (!carousel || reviewVideoInstances.has(carousel)) return;
    const rail = carousel.querySelector('[data-testimonial-track]');
    if (!rail) return;
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeVideo = null;

    // The marquee only drives motion when .marquee-on is set and motion is allowed. Reduced-motion /
    // no-JS visitors get a manual scroll rail, where we just play the video in place (no centring).
    function marqueeOn() {
      return carousel.classList.contains('marquee-on') && !reducedMotionQuery.matches;
    }

    function railTranslateX() {
      const transform = getComputedStyle(rail).transform;
      if (!transform || transform === 'none') return 0;
      const parts = transform.slice(transform.indexOf('(') + 1, -1).split(',').map(parseFloat);
      // matrix(a,b,c,d,e,f) -> tx=parts[4]; matrix3d(...) -> tx=parts[12]
      return (parts.length === 16 ? parts[12] : parts[4]) || 0;
    }

    function railVar(name) {
      return parseFloat(getComputedStyle(rail).getPropertyValue(name)) || 0;
    }

    // Centre the EXACT card the user clicked (its own copy, never a duplicate — with only a few
    // reviews the marquee shows each video twice, and the user wants the one they clicked to play).
    //
    // The marquee is identical card-sets translated 0 -> -cycle; the viewport is gap-free only while
    // the rail's translateX sits in [-cycle, 0]. So we compute the translate that puts the clicked
    // card dead-centre, then CLAMP it into that window. For any card that is not right at the loop
    // seam the clamp is a no-op and the card lands perfectly centred; only a card sitting at the very
    // edge of the loop is nudged a little off-centre instead of tearing a blank strip open. Staying
    // in-window also means the marquee resumes on-phase (no jump) when the video pauses/ends.
    function centerClicked(card) {
      if (!marqueeOn()) return;
      const cycle = railVar('--marquee-cycle');
      const centerX = window.innerWidth / 2;
      const currentX = railTranslateX();
      const rect = card.getBoundingClientRect();
      let target = currentX + (centerX - (rect.left + rect.width / 2));
      if (cycle > 0) target = Math.max(-cycle, Math.min(0, target)); // keep inside the gap-free window
      rail.style.transition = 'none';
      rail.style.animation = 'none';
      rail.style.transform = 'translate3d(' + currentX + 'px, 0, 0)';
      void rail.offsetWidth; // apply the frozen state before the glide so there is no jump
      rail.style.transition = 'transform 480ms cubic-bezier(0.22, 1, 0.36, 1)';
      rail.style.transform = 'translate3d(' + target + 'px, 0, 0)';
    }

    // Hand motion back to the CSS marquee, resuming from the rail's CURRENT translateX with no jump.
    // The keyframe runs translate(0) -> translate(-cycle) over `duration`; a negative animation-delay
    // equal to the current phase starts it exactly where the rail sits now.
    function resumeMarquee() {
      if (!carousel.classList.contains('marquee-on')) return;
      const cycle = railVar('--marquee-cycle');
      const duration = railVar('--marquee-duration');
      const x = railTranslateX();
      rail.style.transition = 'none';
      // Revert the `animation` SHORTHAND to the class value FIRST, then set the delay LONGHAND.
      // Order matters: assigning `style.animation` clears every animation-* longhand (incl. delay),
      // so setting the delay before this line would wipe it and the marquee would jump to phase 0.
      rail.style.animation = '';
      rail.style.transform = '';
      if (cycle > 0 && duration > 0) {
        const phase = ((((-x) % cycle) + cycle) % cycle) / cycle; // 0..1 into the loop
        rail.style.animationDelay = (-(phase * duration)) + 's';
      } else {
        rail.style.animationDelay = '';
      }
      window.requestAnimationFrame(() => { rail.style.transition = ''; });
    }

    function markCard(video, playing) {
      const card = video && video.closest('[data-review-video]');
      if (card) card.classList.toggle('is-playing', playing);
    }

    // Re-centre the active card whenever it starts playing. This covers both the initial click and a
    // native-controls replay after a pause (when the marquee had resumed and moved the card away).
    function onPlay() { if (activeVideo) { markCard(activeVideo, true); centerClicked(activeVideo.closest('[data-review-video]')); } }
    function onPause() { if (activeVideo) markCard(activeVideo, false); resumeMarquee(); }
    function onEnded() { if (activeVideo) markCard(activeVideo, false); resumeMarquee(); }

    function detach(video) {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
    }

    // Silence and release the currently-playing video WITHOUT resuming the marquee (the caller does
    // that). Listeners are removed before pause() so its async 'pause' event can't fire onPause.
    function stopActive() {
      if (!activeVideo) return;
      const video = activeVideo;
      activeVideo = null;
      detach(video);
      video.pause();
      video.muted = true;
      markCard(video, false);
    }

    function play(card) {
      const video = card.querySelector('video');
      if (!video) return;
      centerClicked(card); // slide THIS card to centre (clamped to the gap-free window)
      if (activeVideo && activeVideo !== video) stopActive();
      activeVideo = video;
      video.muted = false;
      video.removeAttribute('muted');
      video.loop = false;
      video.removeAttribute('loop');
      video.controls = true;
      video.setAttribute('controls', '');
      video.setAttribute('playsinline', '');
      try { video.currentTime = 0; } catch (error) { /* not seekable yet */ }
      detach(video);
      video.addEventListener('play', onPlay);
      video.addEventListener('pause', onPause);
      video.addEventListener('ended', onEnded);
      markCard(video, true);
      const played = video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
    }

    function onClick(event) {
      const badge = event.target.closest('[data-review-play]');
      const card = event.target.closest('[data-review-video]');
      if (!card || !carousel.contains(card)) return;
      const video = card.querySelector('video');
      if (!video) return;
      // On the card that is already active, clicks on the <video> belong to its native controls
      // (play/pause/seek) - don't restart. The green badge (shown again once paused) restarts it.
      if (!badge && event.target === video && video === activeVideo) return;
      event.preventDefault();
      play(card);
    }

    carousel.addEventListener('click', onClick);

    reviewVideoInstances.set(carousel, () => {
      stopActive();
      carousel.removeEventListener('click', onClick);
      rail.style.transition = '';
      rail.style.animation = '';
      rail.style.transform = '';
      rail.style.animationDelay = '';
      reviewVideoInstances.delete(carousel);
    });
  }

  function initRoot(root) {
    initCarousel(root);
    initComparisonCarousel(root);
    initSatietyRail(root);
    initReviewVideoPlay(root);
  }

  function initAll(scope = document) {
    if (scope.matches?.('.cowmit-satiety-v1')) initRoot(scope);
    scope.querySelectorAll('.cowmit-satiety-v1').forEach(initRoot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-testimonial-carousel]').forEach((carousel) => {
      carouselInstances.get(carousel)?.();
      reviewVideoInstances.get(carousel)?.();
    });
    event.target.querySelectorAll('[data-comparison-carousel]').forEach((comparison) => comparisonInstances.get(comparison)?.());
    event.target.querySelectorAll('[data-satiety-carousel]').forEach((satiety) => satietyInstances.get(satiety)?.());
  });
})();
